import React, { Suspense, useEffect } from 'react';
import styled from 'styled-components';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import {
  ACCOUNT_DETAILS_PATH,
  ACCOUNT_DETAILS_TAB_PATH,
  ACCOUNT_DETAILS_SUBTAB_PATH,
  ACCOUNTS_LIST_PATH,
  ACTIVITY_EXPORT_ONBOARDING_PATH,
  ACTIVITY_EXPORT_LEARN_MORE_PATH,
  ACTIVITY_EXPORT_PATH,
  ACTIVITY_EXPORT_SIGN_PATH,
  ADVERTISE_WITH_US_PATH,
  ANALYTICS_PATH,
  ASSOCIATED_KEYS_LEARN_MORE_PATH,
  ASSOCIATED_KEYS_ONBOARDING_PATH,
  ASSOCIATED_KEYS_PATH,
  BLOCK_DETAILS_PATH,
  BLOCK_LIST_PATH,
  BUY_CSPR_PATH,
  CHANGELOG_PATH,
  CONTRACT_LIST_PATH,
  CONTRACT_PACKAGE_PATH,
  CONTRACT_PACKAGE_TAB_PATH,
  CONTRACT_PACKAGE_SUBTAB_PATH,
  CONTRACT_PACKAGE_OLD_PATH,
  CONTRACT_PATH,
  CREDITS_PATH,
  DELEGATE_STAKE_PATH,
  DELEGATE_STAKE_SIGN_PATH,
  DEPLOY_DETAILS_PATH,
  DEPLOY_LIST_PATH,
  TRANSACTION_LIST_PATH,
  TRANSACTION_DETAILS_PATH,
  DICTIONARY_PATH,
  FAUCET_PATH,
  HOME_PATH,
  MANAGE_ASSOCIATED_KEYS_PATH,
  MANAGE_ASSOCIATED_KEYS_SIGN_PATH,
  NFT_TOKEN_PATH,
  PEERS_PATH,
  PRIVACY_POLICY_PATH,
  PURCHASE_NFT_PATH,
  PURCHASE_NFT_SIGN_PATH,
  SEARCH_RESULTS_PATH,
  SEARCH_PATH,
  TOS_PATH,
  TRANSFER_CSPR_PATH,
  TRANSFER_CSPR_SIGN_PATH,
  UNDELEGATE_STAKE_PATH,
  UNDELEGATE_STAKE_SIGN_PATH,
  UREF_PATH,
  VALIDATOR_AUCTION_PATH,
  VALIDATOR_DETAILS_PATH,
  VALIDATOR_LIST_PATH,
  ACCOUNT_DETAILS_OLD_PATH,
  CONTRACT_FEATURED_LIST_PATH,
} from './paths';

import { useCustomLocation } from './navigation';

import Skeleton from 'react-loading-skeleton';
import { PageTile } from '@make-software/cspr-design';
import { PageLayout } from '../layouts';

import HomeScene from '../scenes/HomeScene';
import PageHeaderWithClick from '../layouts/components/page-header/page-header-with-click';
import PageFooter from '../layouts/components/page-footer';
import Spotlight from '../components/spotlight/spotlight';
import SkipLinks from '../components/skip-link/skip-link';
import CanonicalLink from '../components/canonical-link/canonical-link';
import { useSyncGlobalStateOnRouteChange } from '../services/hooks/use-sync-global-state-on-route-change';
import lazyWithRetry from './lazy-with-retry';

const AccountDetailsScene = lazyWithRetry(
  () => import('../scenes/AccountDetailsScene')
);
const AccountDetailsFeedScene = lazyWithRetry(
  () => import('../scenes/AccountDetailsFeedScene')
);
const BlockDetailsScene = lazyWithRetry(
  () => import('../scenes/BlockDetailsScene')
);
const BlockListScene = lazyWithRetry(() => import('../scenes/BlockListScene'));
const DelegateStakeScene = lazyWithRetry(
  () => import('../scenes/DelegateStakeScene')
);
const DeployDetailsScene = lazyWithRetry(
  () => import('../scenes/DeployDetailsScene')
);
const DeployListScene = lazyWithRetry(
  () => import('../scenes/DeployListScene')
);
const PeerListScene = lazyWithRetry(() => import('../scenes/PeerListScene'));
const PrivacyPolicyScene = lazyWithRetry(
  () => import('../scenes/PrivacyPolicyScene')
);
const TermsOfServiceScene = lazyWithRetry(
  () => import('../scenes/TermsOfServiceScene')
);
const TransferCSPRScene = lazyWithRetry(
  () => import('../scenes/TransferCSPRScene')
);
const UndelegateStakeScene = lazyWithRetry(
  () => import('../scenes/UndelegateStakeScene')
);
const ValidatorDetailsScene = lazyWithRetry(
  () => import('../scenes/ValidatorDetailsScene')
);
const ValidatorListScene = lazyWithRetry(
  () => import('../scenes/ValidatorListScene')
);
const ValidatorAuctionScene = lazyWithRetry(
  () => import('../scenes/ValidatorAuctionScene')
);
const FaucetScene = lazyWithRetry(() => import('../scenes/FaucetScene'));
const URefScene = lazyWithRetry(() => import('../scenes/URefScene'));
const ChangelogScene = lazyWithRetry(() => import('../scenes/ChangelogScene'));
const ContractScene = lazyWithRetry(() => import('../scenes/ContractScene'));
const ContractPackageScene = lazyWithRetry(
  () => import('../scenes/ContractPackageScene')
);
const ContractPackageFeedScene = lazyWithRetry(
  () => import('../scenes/ContractPackageFeedScene')
);
const SearchRedirectScene = lazyWithRetry(
  () => import('../scenes/SearchRedirectScene')
);
const SearchResultsScene = lazyWithRetry(
  () => import('../scenes/SearchResultsScene')
);
const DictionaryScene = lazyWithRetry(
  () => import('../scenes/DictionaryScene')
);
const AccountsListScene = lazyWithRetry(
  () => import('../scenes/AccountsListScene')
);
const ContractListScene = lazyWithRetry(
  () => import('../scenes/ContractListScene')
);
const ContractFeaturedListScene = lazyWithRetry(
  () => import('../scenes/ContractFeaturedListScene')
);
const NftTokenDetailsScene = lazyWithRetry(
  () => import('../scenes/NftTokenDetailsScene')
);
const CreditsScene = lazyWithRetry(() => import('../scenes/CreditsScene'));
const ActivityExportScene = lazyWithRetry(
  () => import('../scenes/ActivityExportScene')
);
const PurchaseActivityExportNFT = lazyWithRetry(
  () => import('../scenes/PurchaseActivityExportNFTScene')
);
const ActivityExportOnboardingScene = lazyWithRetry(
  () =>
    import('../scenes/ActivityExportOnboardingScene/ActivityExportOnboarding')
);
const ActivityExportLearnMoreScene = lazyWithRetry(
  () =>
    import('../scenes/ActivityExportOnboardingScene/ActivityExportLearnMore')
);
const AnalyticsScene = lazyWithRetry(() => import('../scenes/AnalyticsScene'));
const AssociatedKeysLearnMore = lazyWithRetry(
  () =>
    import('../scenes/AssociatedKeysScene/components/AssociatedKeysLearnMore')
);
const AssociatedKeysOnboarding = lazyWithRetry(
  () =>
    import('../scenes/AssociatedKeysScene/components/AssociatedKeysOnboarding')
);
const AccountAssociatedKeys = lazyWithRetry(
  () => import('../scenes/AssociatedKeysScene/components/AccountAssociatedKeys')
);

const ManageAssociatedKeysScene = lazyWithRetry(
  () => import('../scenes/ManageAssociatedKeysScene')
);
const AdvertiseWithUsScene = lazyWithRetry(
  () => import('../scenes/AdvertiseWithUsScene')
);

export const Container = styled.div(({ theme }) => ({
  minWidth: theme.minWidth,
  display: 'flex',
  flexFlow: 'column nowrap',
  alignItems: 'center',
  backgroundColor: theme.styleguideColors.backgroundSecondary,
  color: theme.styleguideColors.contentPrimary,
  minHeight: '60vh',
}));

export const StyledSkeletonContainer = styled.div(({ theme }) => ({
  width: '100%',
  height: '60vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
}));

export const Loading = () => {
  return (
    <PageLayout renderHeader={() => <div />}>
      <PageTile>
        <StyledSkeletonContainer>
          <Skeleton width={200} height={20} />
        </StyledSkeletonContainer>
      </PageTile>
    </PageLayout>
  );
};

const ScrollToTop = () => {
  const { pathname, state } = useCustomLocation();

  useEffect(() => {
    if (state?.preservedScroll) {
      return;
    }

    window.scrollTo(0, 0);

    return () => {
      // unsubscribe();
    };
  }, [pathname]);

  return null;
};

const PageWithLoading = ({ Component, ...rest }) => {
  useSyncGlobalStateOnRouteChange();
  return (
    <Suspense fallback={<Loading />}>
      <Component {...rest} />
    </Suspense>
  );
};

export default React.memo(() => {
  return (
    <BrowserRouter>
      <CanonicalLink />
      <SkipLinks />
      <ScrollToTop />
      <Spotlight />
      <div className={'new-node-lunch-spotlight-target'} />
      <Container>
        <PageHeaderWithClick />
        <Routes>
          <Route
            path={HOME_PATH}
            element={<PageWithLoading Component={HomeScene} />}
          />
          <Route
            path={BUY_CSPR_PATH}
            element={<PageWithLoading Component={HomeScene} />}
          />
          <Route
            path={ACCOUNT_DETAILS_OLD_PATH}
            element={<PageWithLoading Component={AccountDetailsScene} />}
          />
          <Route
            path={ACCOUNT_DETAILS_PATH}
            element={<PageWithLoading Component={AccountDetailsFeedScene} />}
          />
          <Route
            path={ACCOUNT_DETAILS_TAB_PATH}
            element={<PageWithLoading Component={AccountDetailsFeedScene} />}
          />
          <Route
            path={ACCOUNT_DETAILS_SUBTAB_PATH}
            element={<PageWithLoading Component={AccountDetailsFeedScene} />}
          />
          <Route
            path={BLOCK_LIST_PATH}
            element={<PageWithLoading Component={BlockListScene} />}
          />
          <Route
            path={BLOCK_DETAILS_PATH}
            element={<PageWithLoading Component={BlockDetailsScene} />}
          />
          <Route
            path={SEARCH_RESULTS_PATH}
            element={<PageWithLoading Component={SearchResultsScene} />}
          />
          <Route
            path={SEARCH_PATH}
            element={<PageWithLoading Component={SearchRedirectScene} />}
          />
          <Route
            path={ACCOUNTS_LIST_PATH}
            element={<PageWithLoading Component={AccountsListScene} />}
          />
          <Route
            path={CONTRACT_LIST_PATH}
            element={<PageWithLoading Component={ContractListScene} />}
          />
          <Route
            path={CONTRACT_FEATURED_LIST_PATH}
            element={<PageWithLoading Component={ContractFeaturedListScene} />}
          />
          <Route
            path={CONTRACT_PATH}
            element={<PageWithLoading Component={ContractScene} />}
          />
          <Route
            path={CONTRACT_PACKAGE_OLD_PATH}
            element={<PageWithLoading Component={ContractPackageScene} />}
          />
          <Route
            path={CONTRACT_PACKAGE_PATH}
            element={<PageWithLoading Component={ContractPackageFeedScene} />}
          />
          <Route
            path={CONTRACT_PACKAGE_TAB_PATH}
            element={<PageWithLoading Component={ContractPackageFeedScene} />}
          />
          <Route
            path={CONTRACT_PACKAGE_SUBTAB_PATH}
            element={<PageWithLoading Component={ContractPackageFeedScene} />}
          />
          <Route
            path={DEPLOY_LIST_PATH}
            element={<PageWithLoading Component={DeployListScene} />}
          />
          <Route
            path={DEPLOY_DETAILS_PATH}
            element={<PageWithLoading Component={DeployDetailsScene} />}
          />
          <Route
            path={TRANSACTION_LIST_PATH}
            element={<PageWithLoading Component={DeployListScene} />}
          />
          <Route
            path={TRANSACTION_DETAILS_PATH}
            element={<PageWithLoading Component={DeployDetailsScene} />}
          />
          <Route
            path={VALIDATOR_DETAILS_PATH}
            element={<PageWithLoading Component={ValidatorDetailsScene} />}
          />
          <Route
            path={VALIDATOR_LIST_PATH}
            element={<PageWithLoading Component={ValidatorListScene} />}
          />
          <Route
            path={VALIDATOR_AUCTION_PATH}
            element={<PageWithLoading Component={ValidatorAuctionScene} />}
          />
          <Route
            path={FAUCET_PATH}
            element={<PageWithLoading Component={FaucetScene} />}
          />
          <Route
            path={PEERS_PATH}
            element={<PageWithLoading Component={PeerListScene} />}
          />
          <Route
            path={TRANSFER_CSPR_PATH}
            element={<PageWithLoading Component={TransferCSPRScene} />}
          />
          <Route
            path={TRANSFER_CSPR_SIGN_PATH}
            element={<PageWithLoading Component={TransferCSPRScene} />}
          />
          <Route
            path={DELEGATE_STAKE_PATH}
            element={<PageWithLoading Component={DelegateStakeScene} />}
          />
          <Route
            path={DELEGATE_STAKE_SIGN_PATH}
            element={<PageWithLoading Component={DelegateStakeScene} />}
          />
          <Route
            path={UNDELEGATE_STAKE_PATH}
            element={<PageWithLoading Component={UndelegateStakeScene} />}
          />
          <Route
            path={UNDELEGATE_STAKE_SIGN_PATH}
            element={<PageWithLoading Component={UndelegateStakeScene} />}
          />
          <Route
            path={TOS_PATH}
            element={<PageWithLoading Component={TermsOfServiceScene} />}
          />
          <Route
            path={PRIVACY_POLICY_PATH}
            element={<PageWithLoading Component={PrivacyPolicyScene} />}
          />
          <Route
            path={CHANGELOG_PATH}
            element={<PageWithLoading Component={ChangelogScene} />}
          />
          <Route
            path={CREDITS_PATH}
            element={<PageWithLoading Component={CreditsScene} />}
          />
          <Route
            path={UREF_PATH}
            element={<PageWithLoading Component={URefScene} />}
          />
          <Route
            path={DICTIONARY_PATH}
            element={<PageWithLoading Component={DictionaryScene} />}
          />
          <Route
            path={NFT_TOKEN_PATH}
            element={<PageWithLoading Component={NftTokenDetailsScene} />}
          />
          <Route
            path={ACTIVITY_EXPORT_PATH}
            element={<PageWithLoading Component={ActivityExportScene} />}
          />
          <Route
            path={ACTIVITY_EXPORT_SIGN_PATH}
            element={<PageWithLoading Component={ActivityExportScene} />}
          />
          <Route
            path={PURCHASE_NFT_PATH}
            element={<PageWithLoading Component={PurchaseActivityExportNFT} />}
          />
          <Route
            path={PURCHASE_NFT_SIGN_PATH}
            element={<PageWithLoading Component={PurchaseActivityExportNFT} />}
          />
          <Route
            path={ACTIVITY_EXPORT_ONBOARDING_PATH}
            element={
              <PageWithLoading Component={ActivityExportOnboardingScene} />
            }
          />
          <Route
            path={ACTIVITY_EXPORT_LEARN_MORE_PATH}
            element={
              <PageWithLoading Component={ActivityExportLearnMoreScene} />
            }
          />
          <Route
            path={ANALYTICS_PATH}
            element={<PageWithLoading Component={AnalyticsScene} />}
          />
          <Route
            path={MANAGE_ASSOCIATED_KEYS_PATH}
            element={<PageWithLoading Component={ManageAssociatedKeysScene} />}
          />
          <Route
            path={MANAGE_ASSOCIATED_KEYS_SIGN_PATH}
            element={<PageWithLoading Component={ManageAssociatedKeysScene} />}
          />
          <Route
            path={ASSOCIATED_KEYS_ONBOARDING_PATH}
            element={<PageWithLoading Component={AssociatedKeysOnboarding} />}
          />
          <Route
            path={ASSOCIATED_KEYS_PATH}
            element={<PageWithLoading Component={AccountAssociatedKeys} />}
          />
          <Route
            path={ASSOCIATED_KEYS_LEARN_MORE_PATH}
            element={<PageWithLoading Component={AssociatedKeysLearnMore} />}
          />
          <Route
            path={ADVERTISE_WITH_US_PATH}
            element={<PageWithLoading Component={AdvertiseWithUsScene} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <PageFooter />
      </Container>
    </BrowserRouter>
  );
});
